import type { TasksDeepLinkSupportResponse } from "shared-core/types/agent/activity.ts";
import { buildAgentNavigateUrl } from "shared-core/utils/agent-deep-link.ts";
import { detectBrowserDesktopOs } from "shared-core/utils/browser-os.ts";
import type { RouteLocationNormalizedGeneric, Router } from "vue-router";

const ATTEMPTED_KEY = "openInAppAttempted";

const HANDOFF_PATH = "/tasks";
const FORWARDED_QUERY_PARAMS = ["open"];
const CAMPAIGN_QUERY_PREFIX = "utm_";

let probe: Promise<boolean> | null = null;

export function isHandoffPath(path: string): boolean {
  return path.replace(/\/+$/, "") === HANDOFF_PATH;
}

// utm_* aside, an unknown param is browser state the agent can't reproduce.
export function isHandoffQuery(
  query: RouteLocationNormalizedGeneric["query"],
): boolean {
  return Object.entries(query).every(
    ([param, value]) =>
      param.startsWith(CAMPAIGN_QUERY_PREFIX) ||
      (FORWARDED_QUERY_PARAMS.includes(param) && typeof value === "string"),
  );
}

export function buildHandoffUrl(
  query: RouteLocationNormalizedGeneric["query"],
): string {
  const forwarded: Record<string, string> = {};
  for (const param of FORWARDED_QUERY_PARAMS) {
    const value = query[param];
    if (typeof value === "string" && value.length > 0) {
      forwarded[param] = value;
    }
  }
  return buildAgentNavigateUrl(HANDOFF_PATH, forwarded);
}

// Plain fetch: the Api class logs out on 401 and drags in the router graph.
async function fetchTasksDeepLinkSupported(os: string): Promise<boolean> {
  try {
    const response = await fetch(
      `/api/agent/tasks-deep-link-support?os=${os}`,
      { headers: { accept: "application/json" } },
    );
    if (!response.ok) return false;
    const data = (await response.json()) as TasksDeepLinkSupportResponse;
    return data.tasksDeepLinkSupported;
  } catch {
    return false;
  }
}

async function maybeHandoffToAgent(
  to: RouteLocationNormalizedGeneric,
  router: Router,
): Promise<void> {
  if (!isHandoffPath(to.path)) return;
  if (!isHandoffQuery(to.query)) return;
  if (sessionStorage.getItem(ATTEMPTED_KEY) === "true") return;
  const browserOs = detectBrowserDesktopOs();
  if (browserOs === null) return;

  probe ??= fetchTasksDeepLinkSupported(browserOs);
  if (!(await probe)) return;

  if (router.currentRoute.value.fullPath !== to.fullPath) return;

  sessionStorage.setItem(ATTEMPTED_KEY, "true");
  window.location.href = buildHandoffUrl(to.query);
}

function whenPageLoaded(): Promise<void> {
  if (document.readyState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    window.addEventListener("load", () => resolve(), { once: true });
  });
}

export function initOpenInApp(router: Router): void {
  // One-shot on the initial route; in-app navigation must never yank the user out.
  void Promise.all([router.isReady(), whenPageLoaded()]).then(() => {
    void maybeHandoffToAgent(router.currentRoute.value, router);
  });
}
